import { BodyPart } from "~/api/API";
import { toSentenceCase } from "~/common/converters";

/**
 * Anatomical ordering of body parts from top to bottom, right to left
 * Used for consistent ordering in tables and displays
 *
 * IMPORTANT: This array must contain ALL values from the BodyPart enum.
 * TypeScript will error if any BodyPart value is missing, ensuring exhaustiveness.
 *
 * NOTE: This is the source of truth for body part ordering.
 * Keep in sync with services/models/common.py (BODY_PART_ANATOMICAL_ORDER)
 */
export const BODY_PART_ANATOMICAL_ORDER: readonly BodyPart[] = [
  // Head and neck region
  BodyPart.HEAD,
  BodyPart.FACE,
  BodyPart.EYE,
  BodyPart.EAR,
  BodyPart.NOSE,
  BodyPart.MOUTH,
  BodyPart.JAW,
  BodyPart.TEETH,

  // Spine (top to bottom)
  BodyPart.CERVICAL_SPINE,

  // Upper extremities (general, then shoulders)
  BodyPart.RIGHT_UPPER_EXTREMITY,
  BodyPart.LEFT_UPPER_EXTREMITY,
  BodyPart.RIGHT_SHOULDER,
  BodyPart.LEFT_SHOULDER,
  BodyPart.RIGHT_ARM,
  BodyPart.LEFT_ARM,
  BodyPart.RIGHT_ELBOW,
  BodyPart.LEFT_ELBOW,
  BodyPart.RIGHT_FOREARM,
  BodyPart.LEFT_FOREARM,
  BodyPart.RIGHT_WRIST,
  BodyPart.LEFT_WRIST,
  BodyPart.RIGHT_HAND,
  BodyPart.LEFT_HAND,

  // Fingers (right then left)
  BodyPart.RIGHT_THUMB,
  BodyPart.LEFT_THUMB,
  BodyPart.RIGHT_INDEX_FINGER,
  BodyPart.LEFT_INDEX_FINGER,
  BodyPart.RIGHT_MIDDLE_FINGER,
  BodyPart.LEFT_MIDDLE_FINGER,
  BodyPart.RIGHT_RING_FINGER,
  BodyPart.LEFT_RING_FINGER,
  BodyPart.RIGHT_LITTLE_FINGER,
  BodyPart.LEFT_LITTLE_FINGER,

  // Chest and torso
  BodyPart.CHEST,
  BodyPart.ABDOMEN,
  BodyPart.THORACIC_SPINE,
  BodyPart.LUMBAR_SPINE,

  // Axial pelvic ring (below the lumbar spine, above the hips)
  BodyPart.SACRUM,
  BodyPart.COCCYX,
  BodyPart.PELVIS,

  // Lower extremities (general, then hips)
  BodyPart.RIGHT_LOWER_EXTREMITY,
  BodyPart.LEFT_LOWER_EXTREMITY,
  BodyPart.RIGHT_HIP,
  BodyPart.LEFT_HIP,

  // Legs (right then left, top to bottom)
  BodyPart.RIGHT_LEG,
  BodyPart.LEFT_LEG,
  BodyPart.RIGHT_KNEE,
  BodyPart.LEFT_KNEE,
  BodyPart.RIGHT_LOWER_LEG,
  BodyPart.LEFT_LOWER_LEG,
  BodyPart.RIGHT_ANKLE,
  BodyPart.LEFT_ANKLE,
  BodyPart.RIGHT_FOOT,
  BodyPart.LEFT_FOOT,

  // Systems
  BodyPart.NERVE_SYSTEM,
  BodyPart.DIGESTIVE_SYSTEM,
  BodyPart.CIRCULATORY_SYSTEM,
  BodyPart.RESPIRATORY_SYSTEM,
  BodyPart.REPRODUCTIVE_SYSTEM,
  BodyPart.EXCRETORY_SYSTEM,
  BodyPart.ENDOCRINE_SYSTEM,
  BodyPart.SKIN,

  // Psych (DWC groups these under the nervous system)
  BodyPart.STRESS,
  BodyPart.PSYCH,
] satisfies readonly BodyPart[];

// Compile-time exhaustiveness check: ensures ALL BodyPart enum values are present in the array
// This will cause a TypeScript compilation error if any BodyPart is missing or invalid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _exhaustivenessCheck: BodyPart extends (typeof BODY_PART_ANATOMICAL_ORDER)[number]
  ? (typeof BODY_PART_ANATOMICAL_ORDER)[number] extends BodyPart
    ? true
    : "Error: BODY_PART_ANATOMICAL_ORDER contains invalid BodyPart values"
  : "Error: BODY_PART_ANATOMICAL_ORDER is missing some BodyPart enum values" =
  true;

/**
 * Sort body parts according to anatomical order
 * Accepts both BodyPart enum values and their string representations
 * @param bodyParts Array of body parts (enum or string) to sort
 * @returns Sorted array of body parts
 *
 * @example
 * // With BodyPart enums
 * sortBodyPartsAnatomically([BodyPart.LEFT_FOOT, BodyPart.HEAD])
 * // => [BodyPart.HEAD, BodyPart.LEFT_FOOT]
 *
 * @example
 * // With strings
 * sortBodyPartsAnatomically(["LEFT_FOOT", "HEAD"])
 * // => ["HEAD", "LEFT_FOOT"]
 */
export function sortBodyPartsAnatomically<T extends BodyPart | string>(
  bodyParts: readonly T[],
): T[] {
  return [...bodyParts].sort((a, b) => {
    const indexA = BODY_PART_ANATOMICAL_ORDER.indexOf(a as BodyPart);
    const indexB = BODY_PART_ANATOMICAL_ORDER.indexOf(b as BodyPart);

    // If both are in the order list, sort by position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the order list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the order list, sort alphabetically
    return String(a).localeCompare(String(b));
  });
}

/**
 * Sort body part display strings according to anatomical order
 * Handles human-readable strings like "Right shoulder", "Left foot"
 * @param displayStrings Array of body part display strings to sort
 * @returns Sorted array of display strings
 *
 * @example
 * sortBodyPartDisplayStrings(["Left foot", "Head", "Right shoulder"])
 * // => ["Head", "Right shoulder", "Left foot"]
 */
export function sortBodyPartDisplayStrings(
  displayStrings: readonly string[],
): string[] {
  return [...displayStrings].sort((a, b) => {
    // Convert display strings back to BodyPart enums
    const enumA = displayToBodyPart(a);
    const enumB = displayToBodyPart(b);

    // Both are valid body parts - use anatomical order
    if (enumA !== null && enumB !== null) {
      const indexA = BODY_PART_ANATOMICAL_ORDER.indexOf(enumA);
      const indexB = BODY_PART_ANATOMICAL_ORDER.indexOf(enumB);
      return indexA - indexB;
    }

    // If only one is a valid body part, prioritize it
    if (enumA !== null) return -1;
    if (enumB !== null) return 1;

    // Neither is a valid body part - sort alphabetically
    return a.localeCompare(b);
  });
}

/**
 * Convert BodyPart enum to display string
 * @param bodyPart - The BodyPart enum value
 * @returns Human-readable display string
 * @example bodyPartToDisplay(BodyPart.LOWER_BACK) => "Lower back"
 */
export const bodyPartToDisplay = (bodyPart: BodyPart): string => {
  return toSentenceCase(bodyPart.replace(/_/g, " "));
};

/**
 * Convert display string back to BodyPart enum
 * @param display - The display string (e.g., "Right shoulder")
 * @returns BodyPart enum value or null if invalid
 * @example displayToBodyPart("Right shoulder") => BodyPart.RIGHT_SHOULDER
 */
export function displayToBodyPart(display: string): BodyPart | null {
  const enumValue = display.toUpperCase().replace(/ /g, "_") as BodyPart;
  return Object.values(BodyPart).includes(enumValue) ? enumValue : null;
}
